import { useEffect, useRef, useState } from 'react';
import { HiChevronDown } from 'react-icons/hi2';

import { IPaginationTable } from './interfaces/IPaginationTable';

const PaginationBottomTable = ({
	itemCount,
	pageCount,
	pageNumber,
	pageSize,
	pageSizeOptions,
	setPageNumber,
	setPageSize,
}: IPaginationTable) => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPageNumber(1);
		setIsDropdownOpen(false);
	};

	return (
		<div className="flex sm:flex-row flex-col w-full items-center justify-between gap-4 text-sm px-6 py-3 rounded-xl glassy-shadow-inset-outward">
			<div className="sm:mr-auto sm:mb-0">
				<span className="mr-2 text-gray-700 font-medium">Page Item</span>
				<div className="relative inline-block" ref={dropdownRef}>
					<button
						className="flex items-center justify-between rounded-lg px-3 py-1.5 pr-8 glassy-shadow-inset-inward focus:outline-none focus:ring-2 focus:ring-purpleBrand-500 text-gray-700 font-medium cursor-pointer min-w-[60px]"
						onClick={() => setIsDropdownOpen(!isDropdownOpen)}
						data-test="page-size-select"
					>
						<span>{pageSize}</span>
						<HiChevronDown
							className={`absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 transition-transform ${
								isDropdownOpen ? 'rotate-180' : ''
							}`}
						/>
					</button>

					{/* Custom Dropdown Menu */}
					{isDropdownOpen && (
						<div className="absolute bg-white bottom-full left-0 mb-1 w-full rounded-lg glassy-shadow-inset-outward z-50 max-h-48 overflow-y-auto">
							{pageSizeOptions.map((option) => (
								<button
									key={option}
									className={`w-full text-center px-2 py-2 text-sm transition-all first:rounded-t-lg last:rounded-b-lg ${
										option === pageSize
											? 'glassy-shadow-inset-inward font-medium'
											: 'text-gray-700 hover:scale-105'
									}`}
									onClick={() => handlePageSizeChange(option)}
									data-test={`page-size-${option}`}
								>
									{option}
								</button>
							))}
						</div>
					)}
				</div>
				<span className="ml-2 text-gray-600">of {itemCount} items</span>
			</div>
			<div className="flex items-center gap-2">
				<button
					className={`${
						pageNumber === 1
							? 'opacity-50 cursor-not-allowed'
							: 'hover:scale-105 transition-transform cursor-pointer'
					} rounded-lg px-3 py-1.5 glassy-shadow-inset-inward text-gray-700 font-medium text-sm`}
					onClick={() => {
						setPageNumber(1);
					}}
					disabled={pageNumber === 1}
					data-test="page-number-first"
				>
					<span>{'<<'}</span>
				</button>
				<button
					className={`${
						pageNumber === 1
							? 'opacity-50 cursor-not-allowed'
							: 'hover:scale-105 transition-transform cursor-pointer'
					} rounded-lg px-3 py-1.5 glassy-shadow-inset-inward text-gray-700 font-medium text-sm`}
					onClick={() => {
						if (pageCount >= 1) {
							setPageNumber(pageNumber - 1);
						}
					}}
					disabled={pageNumber === 1}
					data-test="page-number-previous"
				>
					<span>{'<'}</span>
				</button>
				<span
					className="flex items-center gap-1 px-3 text-gray-700 font-semibold"
					data-test="page-number"
				>
					{pageNumber} of {pageCount}
				</span>
				<button
					className={`${
						pageNumber === pageCount
							? 'opacity-50 cursor-not-allowed'
							: 'hover:scale-105 transition-transform cursor-pointer'
					} rounded-lg px-3 py-1.5 glassy-shadow-inset-inward text-gray-700 font-medium text-sm`}
					onClick={() => {
						if (pageCount <= pageCount) {
							setPageNumber(pageNumber + 1);
						}
					}}
					disabled={pageNumber === pageCount}
					data-test="page-number-next"
				>
					<span>{'>'}</span>
				</button>
				<button
					className={`${
						pageNumber === pageCount
							? 'opacity-50 cursor-not-allowed'
							: 'hover:scale-105 transition-transform cursor-pointer'
					} rounded-lg px-3 py-1.5 glassy-shadow-inset-inward text-gray-700 font-medium text-sm`}
					onClick={() => {
						if (pageNumber !== pageCount) {
							setPageNumber(pageCount);
						}
					}}
					disabled={pageNumber === pageCount}
					data-test="page-number-last"
				>
					<span>{'>>'}</span>
				</button>
			</div>
		</div>
	);
};

export default PaginationBottomTable;
